/**
 * One question's results — a different chart per type, the same switch the renderer uses.
 *
 * **Four levels, in this order.** The question is the title, because it is the subject. Then the
 * one number that answers it (a mean, a share of yes, an NPS). Then the distribution, quieter.
 * Then the footer: the `key`, the type and how many answered — the identifier is the least
 * important thing on the card, and it used to be the first thing the eye landed on.
 *
 * **Not every type is a list of bars.** A list of full-width bars turns an NPS into eleven rows and
 * a grid into twelve, towers standing next to a three-line text card. So the two that do not fit
 * the shape get their own: the NPS buckets as one segmented bar plus a compact 0–10 histogram, and
 * each grid row as one line with a segmented scale and the average marked on it.
 *
 * Three things this card is careful about, each because the obvious rendering lies:
 *
 * - **the empty buckets stay.** `RATING` comes back with the whole declared scale and `NPS` with
 *   0–10, zeros included, and an empty rail says "ninguém marcou" rather than looking broken;
 * - **multiple choice sums past 100%,** and says so, because the percentage is of whoever answered
 *   the question, not of the clicks;
 * - **a grid row's average is a position on the scale**, so it is drawn where it falls and printed
 *   next to the option it lands on. "3,4" alone is not a finding.
 *
 * **Colour carries no opinion** on a yes/no question. Green on "Sim" would claim yes is the good
 * answer, and the next boolean is "Você teve dificuldade?". Semantic colour is kept for where it
 * genuinely means something: the NPS buckets and the opt-out reasons.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { useState } from 'react';
import type { JSX } from 'react';
import type {
  SurveyMatrixRowResult,
  SurveyQuestionResult,
  SurveyResultsFilter,
} from '../../../Types/Surveys.types';
import { cutBaseSentence } from '../../../Utils/resultsCut';
import {
  QUESTION_TYPE_LABELS,
  formatAverage,
  formatCount,
  formatNpsScore,
  formatPercent,
  matrixAverageLabel,
  npsSegments,
  scaleMarkerPosition,
} from '../../../Utils/surveyFormat';
import { DistributionBar } from '@/Shared/components/DistributionBar/DistributionBar';
import { ReachChart } from '@/Shared/components/ReachChart/ReachChart';
import { SegmentedBar } from '@/Shared/components/SegmentedBar/SegmentedBar';

/** The base of a cut number reads as part of the number, not as another footer crumb. */
const cutBaseStyle = css`
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
`;

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  background: var(--color-surface);
  min-width: 0;
`;

const questionStyle = css`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
  text-wrap: pretty;
`;

const headlineStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-xs);
  flex-wrap: wrap;
`;

const headlineValueStyle = css`
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const headlineHintStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const barsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** The footer: identifier, type, how many answered, and the caveat. All quiet, all last. */
const footerStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px var(--space-xs);
  margin-top: auto;
  padding-top: var(--space-s);
  border-top: 1px solid var(--color-divider);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/** Monospace only here: the `key` is a code, everything else on the card is prose. */
const keyStyle = css`
  font-family: var(--font-mono);
`;

const noteStyle = css`
  flex: 1 1 100%;
  margin: 0;
  text-wrap: pretty;
`;

const legendStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs) var(--space-m);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const legendItemStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const swatchStyle = css`
  width: 10px;
  height: 10px;
  border-radius: 3px;
`;

/** One aspect of a grid: label, the scale as one bar, the average beside it. */
const matrixRowStyle = css`
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(90px, 10fr) 132px;
  align-items: center;
  gap: var(--space-s);
  padding: 6px 0;
`;

const matrixLabelStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text);
  overflow-wrap: anywhere;
`;

const matrixAverageStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  text-align: right;
`;

const matrixAverageValueStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
`;

const matrixAverageHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const disclosureStyle = css`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-2xs);
  cursor: pointer;
  text-decoration: underline;
`;

const rowDetailStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-xs) 0 var(--space-s);
`;

const npsBucketsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const npsLegendStyle = css`
  display: flex;
  justify-content: space-between;
  gap: var(--space-s);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const npsLegendItemStyle = css`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
`;

const npsCountStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
`;

const linkStyle = css`
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  text-decoration: underline;
`;

/** The one accent a plain distribution gets. No valence: a bar is a count, not a verdict. */
const NEUTRAL = 'var(--color-primary)';

/**
 * The scale, light to dark, in steps the eye can actually separate.
 *
 * An ordinal scale has to read as an order — the first level is the least of the thing and the
 * last is the most — so the ramp goes one way and the gaps between the steps are wide enough to
 * tell apart at the width of a segment a few pixels across. Beyond four levels the last colour
 * repeats, which is honest: past that many levels the segments are too thin to identify by colour
 * and the numbers under "Ver os níveis" are the way to read them.
 */
const SCALE_COLORS = [
  'var(--gl-blue-10)',
  'var(--gl-blue-50)',
  'var(--gl-blue-90)',
  'var(--gl-blue-dark)',
];

export interface QuestionResultCardProps {
  question: SurveyQuestionResult;
  /**
   * The cut this card's numbers were counted under, when there is one (R49).
   *
   * It is not decoration: `answered` under a cut is "of the 40 who marked X", and the same card
   * with no base stated is the 30%-or-75% reading this feature exists to end. The base goes in the
   * footer, beside the count it qualifies — never as a note at the bottom of the screen.
   */
  cut?: SurveyResultsFilter;
  /** Opens the comment feed filtered to this question, for the text types. */
  onReadComments: (questionKey: string) => void;
}

export const QuestionResultCard = ({
  question,
  cut,
  onReadComments,
}: QuestionResultCardProps): JSX.Element => {
  const id = `survey-result-question-${question.key}`;
  const [openRow, setOpenRow] = useState<string | null>(null);

  const scale = question.scale ?? [];
  const scaleColor = (index: number): string =>
    SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];

  const distributionBars = (): JSX.Element => {
    const entries = question.distribution ?? [];
    const total = entries.reduce((sum, entry) => sum + entry.count, 0);

    return (
      <div className={barsStyle}>
        {entries.map(entry => (
          <DistributionBar
            key={String(entry.value)}
            label={String(entry.value)}
            value={entry.count}
            total={total}
            color={NEUTRAL}
            emptyText='ninguém deu esta nota'
          />
        ))}
      </div>
    );
  };

  const matrixRow = (row: SurveyMatrixRowResult): JSX.Element => {
    const total = row.distribution.reduce((sum, entry) => sum + entry.count, 0);
    const segments = scale.map((level, index) => ({
      label: level.label,
      value: row.distribution.find(entry => entry.value === level.value)?.count ?? 0,
      color: scaleColor(index),
    }));
    const averageLabel = matrixAverageLabel(row.average, question.scale);
    const [averageValue, ...rest] = averageLabel.split(' — ');
    const isOpen = openRow === row.key;

    return (
      <div key={row.key}>
        <div className={matrixRowStyle}>
          <span className={matrixLabelStyle}>{row.label}</span>
          <SegmentedBar
            segments={segments}
            markerAt={scaleMarkerPosition(row.average, scale.length)}
            markerLabel={`Média: ${averageLabel}`}
            label={`${row.label}: ${averageLabel}`}
          />
          <span className={matrixAverageStyle}>
            <span className={matrixAverageValueStyle}>{averageValue}</span>
            {rest.length > 0 ? (
              <span className={matrixAverageHintStyle}>{rest.join(' — ')}</span>
            ) : null}
          </span>
        </div>

        <button
          type='button'
          id={`${id}-row-${row.key}-toggle`}
          className={disclosureStyle}
          aria-expanded={isOpen}
          onClick={() => {
            setOpenRow(current => (current === row.key ? null : row.key));
          }}
        >
          {isOpen ? 'Esconder os níveis' : 'Ver os níveis'}
        </button>

        {isOpen ? (
          <div className={rowDetailStyle}>
            {scale.map((level, index) => (
              <DistributionBar
                key={level.value}
                label={level.label}
                value={row.distribution.find(entry => entry.value === level.value)?.count ?? 0}
                total={total}
                color={scaleColor(index)}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <article id={id} className={cardStyle}>
      <span className={questionStyle}>{question.label}</span>

      {question.type === 'RATING' ? (
        <>
          <div className={headlineStyle}>
            <span className={headlineValueStyle}>{formatAverage(question.average)}</span>
            <span className={headlineHintStyle}>média das notas dadas</span>
          </div>
          {distributionBars()}
        </>
      ) : null}

      {question.type === 'NPS' ? (
        <>
          <div className={headlineStyle}>
            <span className={headlineValueStyle}>{formatNpsScore(question.score)}</span>
            <span className={headlineHintStyle}>NPS — de −100 a 100, não é porcentagem</span>
          </div>

          <div className={npsBucketsStyle}>
            <SegmentedBar
              segments={npsSegments(question)}
              label={`Promotores ${question.promoters ?? 0}, neutros ${question.passives ?? 0}, detratores ${question.detractors ?? 0}`}
            />
            <div className={npsLegendStyle}>
              <span className={npsLegendItemStyle}>
                <span className={swatchStyle} style={{ background: 'var(--color-danger)' }} />
                <span className={npsCountStyle}>{formatCount(question.detractors ?? 0)}</span>
                detratores
              </span>
              <span className={npsLegendItemStyle}>
                <span
                  className={swatchStyle}
                  style={{ background: 'var(--color-border-strong)' }}
                />
                <span className={npsCountStyle}>{formatCount(question.passives ?? 0)}</span>
                neutros
              </span>
              <span className={npsLegendItemStyle}>
                <span className={swatchStyle} style={{ background: 'var(--color-success)' }} />
                <span className={npsCountStyle}>{formatCount(question.promoters ?? 0)}</span>
                promotores
              </span>
            </div>
          </div>

          <ReachChart
            height={72}
            values={(question.distribution ?? []).map(entry => entry.count)}
            labels={(question.distribution ?? []).map(entry => String(entry.value))}
            label='Distribuição das notas de 0 a 10'
          />
        </>
      ) : null}

      {question.type === 'BOOLEAN' ? (
        <>
          <div className={headlineStyle}>
            <span className={headlineValueStyle}>
              {question.truePercent === undefined ? '—' : formatPercent(question.truePercent)}
            </span>
            <span className={headlineHintStyle}>responderam que sim</span>
          </div>
          <div className={barsStyle}>
            <DistributionBar
              label='Sim'
              value={question.trueCount ?? 0}
              total={question.answered}
              color={NEUTRAL}
            />
            <DistributionBar
              label='Não'
              value={question.falseCount ?? 0}
              total={question.answered}
              color={NEUTRAL}
            />
          </div>
        </>
      ) : null}

      {question.type === 'SINGLE_CHOICE' || question.type === 'MULTI_CHOICE' ? (
        <div className={barsStyle}>
          {(question.options ?? []).map(option => (
            /* The bar computes the same percentage from the same denominator the BFF used —
               whoever ANSWERED the question — so printing `option.percent` next to it would be
               the same number twice, rounded two different ways. */
            <DistributionBar
              key={option.value}
              label={option.label}
              value={option.count}
              total={question.answered}
              color={NEUTRAL}
              emptyText='ninguém escolheu'
            />
          ))}
        </div>
      ) : null}

      {question.type === 'MATRIX' ? (
        <>
          <div className={legendStyle}>
            {scale.map((level, index) => (
              <span key={level.value} className={legendItemStyle}>
                <span className={swatchStyle} style={{ background: scaleColor(index) }} />
                {level.label}
              </span>
            ))}
          </div>
          <div>{(question.rows ?? []).map(row => matrixRow(row))}</div>
        </>
      ) : null}

      {question.type === 'SHORT_TEXT' || question.type === 'LONG_TEXT' ? (
        <>
          <div className={headlineStyle}>
            <span className={headlineValueStyle}>{formatCount(question.answered)}</span>
            <span className={headlineHintStyle}>pessoas escreveram alguma coisa</span>
          </div>
          <p className={noteStyle} style={{ margin: 0 }}>
            O texto não entra nos agregados.{' '}
            <button
              type='button'
              id={`${id}-read-comments`}
              className={linkStyle}
              onClick={() => {
                onReadComments(question.key);
              }}
            >
              Ler o que escreveram
            </button>
          </p>
        </>
      ) : null}

      <div className={footerStyle}>
        <span className={keyStyle}>{question.key}</span>
        <span>·</span>
        <span>{QUESTION_TYPE_LABELS[question.type]}</span>
        <span>·</span>
        {/* The unit on the number, not in a note: `answered` counts people (R22b). */}
        <span>{formatCount(question.answered)} pessoas responderam</span>

        {/* And WHICH people, when a cut is on: R22b taken to its conclusion. */}
        {cut ? (
          <>
            <span>·</span>
            <span className={cutBaseStyle}>{cutBaseSentence(cut)}</span>
          </>
        ) : null}

        {question.type === 'RATING' ? (
          <p className={noteStyle}>
            A escala vem inteira, inclusive as notas que ninguém deu — some com elas e o gráfico se
            lê como se a escala fosse menor.
          </p>
        ) : null}

        {question.type === 'MULTI_CHOICE' ? (
          <p className={noteStyle}>
            O percentual é sobre quem respondeu a pergunta, não sobre o total de marcações — por
            isso a soma passa de 100%.
          </p>
        ) : null}

        {question.type === 'MATRIX' ? (
          <p className={noteStyle}>
            A média de cada linha é a posição na escala, e a ordem em que as opções foram escritas é
            o que define essa ordem. Linha de sim/não vem sem média, de propósito.
          </p>
        ) : null}
      </div>
    </article>
  );
};
